import { DEFAULT_CHAIN, DEFAULT_CHAIN_ID } from '@/lib/chains'
import { ZORA_TOKEN_CLAIM_CONTRACT_ADDRESS } from '@/lib/contracts'
import { trpc } from '@/lib/trpc'
import { formatNumber } from '@ourzora/utils/dist'
import { ConnectedWallet } from '@privy-io/react-auth'
import * as Sentry from '@sentry/nextjs'
import { useMutation } from '@tanstack/react-query'
import { permitClaimTypedDataDefinition } from '@zoralabs/zora-token-contracts'
import {
  ArrowSquareOut,
  CheckCircle,
  Icon,
  Link,
  Text,
  useToast,
  XCircle,
} from 'reshaped'
import { Address } from 'viem'
import { usePublicClient } from 'wagmi'
import { useSignSmartWalletClaim } from './useSignSmartWalletClaim'

const DEFAULT_DEADLINE_OFFSET = 30n
/**
 * Hook for claiming ZORA tokens with a signature using a Coinbase Smart Wallet
 *
 * This hook handles the process of:
 * 1. Creating a typed signature for token claiming through a smart wallet
 * 2. Finding the owner's index in the smart wallet
 * 3. Generating a signature compatible with the smart wallet
 * 4. Submitting the signature to the API
 * 5. Tracking the transaction status
 *
 * @param wallet - The connected Privy wallet instance (owner of the smart wallet)
 * @param smartWallet - The address of the Coinbase Smart Wallet to claim with
 * @param tokenAmount - The amount of tokens being claimed
 * @returns Mutation object with transaction status and controls
 */
export const useClaimWithSmartWalletWithSignature = ({
  wallet,
  smartWalletAddress,
  onSuccess,
  tokenAmount = 0,
}: {
  wallet?: ConnectedWallet
  smartWalletAddress: Address
  onSuccess?: () => void
  tokenAmount?: number
}) => {
  const toast = useToast()
  const publicClient = usePublicClient()
  const signClaim = useSignSmartWalletClaim()
  const submitClaimWithSignature = trpc.zoraToken.claim.useMutation()
  /**
   * Mutation for signing and submitting a token claim through a smart wallet
   *
   * @param deadlineOffset - Optional bigint value representing seconds until the signature expires (default: 30)
   * @returns Transaction hash of the claim transaction
   */
  return useMutation({
    mutationFn: async () => {
      if (!wallet) {
        throw new Error('No connected wallet found')
      }

      if (!publicClient) {
        throw new Error('Public client not found')
      }

      // Get current timestamp and add deadline offset
      const timestamp = (await publicClient.getBlock()).timestamp
      const deadline = timestamp + DEFAULT_DEADLINE_OFFSET

      const typedData = permitClaimTypedDataDefinition({
        message: {
          user: smartWalletAddress,
          claimTo: smartWalletAddress,
          deadline,
        },
        chainId: DEFAULT_CHAIN_ID,
        claimContract: ZORA_TOKEN_CLAIM_CONTRACT_ADDRESS,
      })

      const signature = await signClaim.mutateAsync({
        smartWalletAddress,
        typedData,
        chainId: DEFAULT_CHAIN_ID,
        signer: wallet,
      })

      const { tx: txHash } = await submitClaimWithSignature.mutateAsync({
        signature,
        from: smartWalletAddress,
        claimTo: smartWalletAddress,
        deadline: deadline.toString(),
        chainId: DEFAULT_CHAIN_ID,
      })

      const receipt = await publicClient.waitForTransactionReceipt({ hash: txHash })
      return { receipt, txHash }
    },
    onError: (error) => {
      // Only check for "user rejected" message
      const errorMessage = error.message.toLowerCase()
      const isUserRejection = errorMessage.includes('user rejected')

      // Only show toast for non-user rejection errors
      if (!isUserRejection) {
        // Log to Sentry
        Sentry.captureException(error, {
          tags: {
            walletType: 'smart',
            action: 'claim_with_smart_wallet',
          },
          extra: {
            smartWalletAddress,
            walletAddress: wallet?.address,
          },
        })

        toast.show({
          text: 'Error Claiming',
          icon: <Icon svg={XCircle} color="critical" size={6} />,
          color: 'neutral',
        })
      }
    },
    onSuccess: (data) => {
      // Show success toast
      const explorerUrl = `${DEFAULT_CHAIN.blockExplorers.default.url}/tx/${data.txHash}`

      toast.show({
        color: 'neutral',
        hideDismiss: true,
        size: 'small',
        timeout: 5000,
        position: 'bottom',
        icon: <Icon svg={CheckCircle} color="positive" size={5} />,
        text: (
          <Text variant="body-3">
            Claimed{' '}
            <Text as="span" weight="medium">
              {formatNumber(Math.floor(tokenAmount))} $ZORA
            </Text>
          </Text>
        ),
        actionsSlot: (
          <Link
            href={explorerUrl}
            variant="plain"
            attributes={{
              target: '_blank',
              rel: 'noopener noreferrer',
              style: {
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              },
            }}
          >
            <Text variant="body-3" weight="regular">
              View on{' '}
              <span style={{ fontWeight: 'var(--rs-font-weight-medium)' }}>Basescan</span>
            </Text>
            <Icon svg={ArrowSquareOut} size={4} />
          </Link>
        ),
      })

      onSuccess?.()
    },
  })
}
